/**
 * CLIENT mirror of the live class access window (F3-G). PURE and
 * testable. The authority is the server (`GET /api/live/sessions/:id/join`, which
 * validates in the academy's time zone); this is only for the pre-join UI
 * (countdown, "LIVE" badge, enabling the "Join" button).
 *
 * Works in FLOATING LOCAL TIME: interprets `date`+`startTime` as the
 * device's time (same as the schedule ICS from F3-A). Enough for the UI; the
 * server makes the real decision.
 */

/** Minutes BEFORE the start when the window opens (default; = `LIVE_JOIN_WINDOW_MIN`). */
export const DEFAULT_JOIN_OPEN_BEFORE_MIN = 15;
/** Minutes AFTER the end during which the join is still allowed (default). */
export const DEFAULT_JOIN_GRACE_AFTER_MIN = 60;

/** Session phase relative to its access window. */
export type JoinWindowPhase = "before" | "open" | "ended";

/** Session times (synced columns from `class_session`). */
export interface SessionTimes {
  /** `YYYY-MM-DD`. */
  date: string | null | undefined;
  /** `HH:MM[:SS]`. */
  startTime: string | null | undefined;
  /** `HH:MM[:SS]`. */
  endTime: string | null | undefined;
}

/** Window state computed at a given instant. */
export interface JoinWindowState {
  phase: JoinWindowPhase;
  /** Epoch ms when the window opens (or null if the times aren't valid). */
  opensAtMs: number | null;
  /** Epoch ms when the window closes. */
  closesAtMs: number | null;
  /** ms until it opens (0 if already open or past). */
  msUntilOpen: number;
  /** Can you join NOW? (phase === "open"). */
  isLiveNow: boolean;
}

/** Normalizes `HH:MM` → `HH:MM:00` and leaves `HH:MM:SS` as is. */
function normalizeTime(t: string): string {
  const parts = t.split(":");
  if (parts.length === 2) {
    return `${t}:00`;
  }
  return t;
}

/**
 * Parses `date`+`time` as floating LOCAL time → epoch ms, or `null` if missing or
 * invalid. `new Date("YYYY-MM-DDTHH:MM:SS")` (without `Z`) uses the local zone.
 */
export function parseLocalMs(
  date: string | null | undefined,
  time: string | null | undefined,
): number | null {
  if (!date || !time) {
    return null;
  }
  const ms = new Date(`${date}T${normalizeTime(time)}`).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Computes the access window state at `nowMs`. Pure.
 */
export function joinWindowState(
  nowMs: number,
  times: SessionTimes,
  openBeforeMin: number = DEFAULT_JOIN_OPEN_BEFORE_MIN,
  graceAfterMin: number = DEFAULT_JOIN_GRACE_AFTER_MIN,
): JoinWindowState {
  const startMs = parseLocalMs(times.date, times.startTime);
  const endMs = parseLocalMs(times.date, times.endTime);
  if (startMs === null || endMs === null) {
    return { phase: "before", opensAtMs: null, closesAtMs: null, msUntilOpen: 0, isLiveNow: false };
  }
  const opensAtMs = startMs - openBeforeMin * 60_000;
  const closesAtMs = endMs + graceAfterMin * 60_000;

  let phase: JoinWindowPhase;
  if (nowMs < opensAtMs) {
    phase = "before";
  } else if (nowMs <= closesAtMs) {
    phase = "open";
  } else {
    phase = "ended";
  }
  return {
    phase,
    opensAtMs,
    closesAtMs,
    msUntilOpen: Math.max(0, opensAtMs - nowMs),
    isLiveNow: phase === "open",
  };
}

/**
 * Formats a countdown (ms) as compact `Xd Xh Xm Xs` (omits leading
 * zeros). Pure; useful for the pre-join.
 */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(total / 86_400);
  const hours = Math.floor((total % 86_400) / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const parts: string[] = [];
  if (days > 0) {
    parts.push(`${days}d`);
  }
  if (days > 0 || hours > 0) {
    parts.push(`${hours}h`);
  }
  if (days > 0 || hours > 0 || mins > 0) {
    parts.push(`${mins}m`);
  }
  parts.push(`${secs}s`);
  return parts.join(" ");
}
